import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EditorState } from '@models/editorState/editorState.model';
import { EditorStateService } from '@services/editorState/editor-state.service';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-top-menu',
  standalone: true,
  imports: [CommonModule,MatIconModule,MatButtonModule,NgxSpinnerModule,MatFormFieldModule,ReactiveFormsModule,MatProgressSpinnerModule,MatMenuModule,MatDividerModule],
  templateUrl: './top-menu.component.html',
  styleUrl: './top-menu.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopMenuComponent {

  editorState!: EditorState;
  private destroy$ = new Subject<void>();

  constructor(
    private editorStateService: EditorStateService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.editorStateService.getEditorState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.editorState = state;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ARCHIVO MENU
  onNewEscrito(): void {
    this.editorStateService.openTab({
      tipo: 'escrito',
      titulo: 'Nuevo Escrito',
      contenido: ''
    });
  }

  onNewPlantilla(): void {
    this.editorStateService.openTab({
      tipo: 'plantilla',
      titulo: 'Nueva Plantilla',
      contenido: ''
    });
  }

  onSave(): void {
    const activeTab = this.editorStateService.getActiveTab();
    if (!activeTab) {
      this.snackBar.open('No hay ningún documento abierto para guardar', 'Cerrar', { duration: 3000 });
      return;
    }

    this.editorStateService.saveActiveTab().subscribe({
      next: () => {
        this.snackBar.open('Documento guardado exitosamente', 'Cerrar', { duration: 2000 });
      },
      error: (error) => {
        console.error('Error guardando documento:', error);
        this.snackBar.open('Error al guardar el documento', 'Cerrar', { duration: 3000 });
      }
    });
  }

  onSaveAll(): void {
    const tabsWithChanges = this.editorState.tabs.filter(tab => tab.hasChanges);

    if (tabsWithChanges.length === 0) {
      this.snackBar.open('No hay cambios para guardar', 'Cerrar', { duration: 2000 });
      return;
    }

    // Implementar guardado múltiple
    console.log('Guardar todos los tabs con cambios:', tabsWithChanges);
  }

  onCloseTab(): void {
    const activeTab = this.editorStateService.getActiveTab();
    if (activeTab) {
      this.editorStateService.closeTab(activeTab.id);
    }
  }

  onCloseAll(): void {
    // Implementar cierre de todos los tabs
    console.log('Cerrar todos los tabs');
  }

  // EDITAR MENU
  onUndo(): void {
    // Implementar deshacer
    console.log('Deshacer');
  }

  onRedo(): void {
    // Implementar rehacer
    console.log('Rehacer');
  }

  onFind(): void {
    // Implementar buscar
    console.log('Buscar');
  }

  onReplace(): void {
    // Implementar reemplazar
    console.log('Reemplazar');
  }

  // HERRAMIENTAS MENU
  onProcessVariables(): void {
    const activeTab = this.editorStateService.getActiveTab();
    if (!activeTab) {
      this.snackBar.open('No hay ningún documento abierto', 'Cerrar', { duration: 3000 });
      return;
    }

    // Implementar procesamiento de variables
    console.log('Procesar variables del documento actual');
    this.snackBar.open('Funcionalidad de procesamiento de variables en desarrollo', 'Cerrar', { duration: 3000 });
  }

  onExportPDF(): void {
    const activeTab = this.editorStateService.getActiveTab();
    if (!activeTab) {
      this.snackBar.open('No hay ningún documento abierto para exportar', 'Cerrar', { duration: 3000 });
      return;
    }

    // Implementar exportación a PDF
    console.log('Exportar a PDF:', activeTab.titulo);
    this.snackBar.open('Funcionalidad de exportación a PDF en desarrollo', 'Cerrar', { duration: 3000 });
  }

  // VER MENU
  onToggleSidebar(): void {
    this.editorStateService.toggleSidebar();
  }

  onShowExplorer(): void {
    this.editorStateService.setSidebarPanel('explorador');
  }

  onShowVariables(): void {
    this.editorStateService.setSidebarPanel('variables');
  }

  onShowPlantillas(): void {
    this.editorStateService.setSidebarPanel('plantillas');
  }

  onShowVersions(): void {
    this.editorStateService.setSidebarPanel('versiones');
  }

  // AYUDA MENU
  onAbout(): void {
    this.snackBar.open('Editor de Escritos v1.0 - Desarrollado con Angular 15', 'Cerrar', { duration: 5000 });
  }

  onKeyboardShortcuts(): void {
    // Implementar diálogo de atajos de teclado
    console.log('Mostrar atajos de teclado');
  }

  // Helper methods
  hasActiveTab(): boolean {
    return !!this.editorState?.activeTabId;
  }

  hasUnsavedChanges(): boolean {
    return this.editorState?.tabs?.some(tab => tab.hasChanges) || false;
  }

  getActiveTabTitle(): string {
    const activeTab = this.editorStateService.getActiveTab();
    return activeTab?.titulo || '';
  }
}

